import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Preferences } from '@capacitor/preferences';

/**
 * Native capability wrappers. Every one degrades to a no-op or a web equivalent, so the same build
 * runs in the browser preview and on a device — never guard a feature behind "is this native?" at
 * the call site, call these instead.
 */

export const isNative = (): boolean => Capacitor.isNativePlatform();

/* ------------------------------------------------------------------ haptics */

/**
 * A HAPTIC VOCABULARY, not a vibrate() call.
 *
 * Haptics are a language with a small grammar, and using the wrong word is worse than silence:
 * buzzing on every tap trains people to ignore the phone, and buzzing on failure the same way you
 * buzz on success removes the only information the feedback carried.
 *
 * The rules this file encodes:
 *  - `selection` for moving between things (tabs, segments, pickers). The lightest tick there is.
 *  - `tap` for a control the user pressed that did something small.
 *  - `success` / `warning` / `error` are NOTIFICATION haptics — distinct patterns the OS reserves
 *    for outcomes. Use them only when something concluded, never for ordinary presses.
 *  - `impact` for a physical event in the UI: a card snapping, a sheet hitting its stop.
 *
 * On web every one is a no-op, and on a device a user who has disabled haptics gets nothing —
 * neither is an error, so failures are swallowed deliberately.
 */

async function safely(run: () => Promise<unknown>): Promise<void> {
	if (!isNative()) return;
	try {
		await run();
	} catch {
		/* haptics unavailable or disabled by the user — never surface this */
	}
}

/** Moving the selection: switching tab, changing a segment, scrolling a picker past a notch. */
export const selectionFeedback = (): Promise<void> => safely(() => Haptics.selectionChanged());

/** A small press that did something. The default for buttons. */
export const tapFeedback = (): Promise<void> => safely(() => Haptics.impact({ style: ImpactStyle.Light }));

/** A physical event: a card snapping into place, a sheet reaching its stop. */
export const impactFeedback = (): Promise<void> => safely(() => Haptics.impact({ style: ImpactStyle.Medium }));

/** Something completed. Saved, sent, finished, streak extended. */
export const successFeedback = (): Promise<void> =>
	safely(() => Haptics.notification({ type: NotificationType.Success }));

/** Something needs attention but is not a failure: offline, nearly due, partially saved. */
export const warningFeedback = (): Promise<void> =>
	safely(() => Haptics.notification({ type: NotificationType.Warning }));

/** Something failed. Pair it with a visible message — a buzz alone explains nothing. */
export const errorFeedback = (): Promise<void> =>
	safely(() => Haptics.notification({ type: NotificationType.Error }));

/** A heavier double-tap for a destructive confirmation. */
export const hitFeedback = (): Promise<void> => safely(() => Haptics.impact({ style: ImpactStyle.Heavy }));

/* ------------------------------------------------------------------ storage */

/**
 * Persistent key-value storage. Capacitor Preferences on device (survives app restarts and is
 * included in device backups), localStorage on web. This is what makes the app usable offline —
 * App Review tests Airplane Mode and rejects an app that shows a blank screen or a browser error.
 */
export async function readStored<T>(key: string): Promise<T | null> {
	try {
		if (isNative()) {
			const { value } = await Preferences.get({ key });
			return value ? (JSON.parse(value) as T) : null;
		}
		const value = localStorage.getItem(key);
		return value ? (JSON.parse(value) as T) : null;
	} catch {
		return null;
	}
}

export async function writeStored<T>(key: string, value: T): Promise<void> {
	try {
		const serialized = JSON.stringify(value);
		if (isNative()) {
			await Preferences.set({ key, value: serialized });
			return;
		}
		localStorage.setItem(key, serialized);
	} catch {
		/* quota or private mode — the app must keep working without persistence */
	}
}
